import { ChangeDetectionStrategy, Component, NgZone } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, distinctUntilChanged, map, Observable } from 'rxjs';
import { Customers } from 'server/shared/collections/customers';
import { Users } from 'server/shared/collections/users';
import { Customer } from 'server/shared/models/customer';
import { RoleEnum } from 'server/shared/models/role.enum';
import { DispoUser } from 'server/shared/models/user';
import { ToastService } from '../../services/toast/toast.service';

@Component({
  selector: 'admin-agency-priorities',
  templateUrl: 'agency-priorities.html',
  styleUrls: ['agency-priorities.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgencyPrioritiesComponent {
  private _tab: string = '';
  customer$ = new BehaviorSubject<Customer | undefined>(undefined);
  user$ = new BehaviorSubject<DispoUser | undefined>(undefined);
  sharedPoolCustomers$ = new BehaviorSubject<Customer[]>([]);

  activePriorities$ = new BehaviorSubject<
    {
      customerName: string;
      customerId: string;
    }[]
  >([]);

  jobTransferPriorities$ = new BehaviorSubject<
    { customerName: string; customerId: string }[]
  >([]);
  employmentTransferPriorities$ = new BehaviorSubject<
    { customerName: string; customerId: string }[]
  >([]);
  sharedPoolPriorities$ = new BehaviorSubject<
    { customerName: string; customerId: string }[]
  >([]);

  tabs$ = new BehaviorSubject<
    {
      label: string;
      value: 'TRANSFERS_JOBS' | 'TRANSFERS_EMPLOYMENTS' | 'SHARED_POOL';
    }[]
  >([
    {
      label: 'jobsTransfers',
      value: 'TRANSFERS_JOBS',
    },
    {
      label: 'employmentsTransfers',
      value: 'TRANSFERS_EMPLOYMENTS',
    },
    {
      label: 'sharedPool',
      value: 'SHARED_POOL',
    },
  ]);

  tabParam$: Observable<
    'TRANSFERS_JOBS' | 'TRANSFERS_EMPLOYMENTS' | 'SHARED_POOL'
  > = this.route.queryParams.pipe(
    map((params) => {
      this._tab = params.tab ?? 'TRANSFERS_JOBS';
      this.setAgencyPriorities();
      return params.tab ?? 'TRANSFERS_JOBS';
    }),
    // distinctUntilChanged is needed in case of other params changing
    distinctUntilChanged(),
  );
  currentUserTracker?: Tracker.Computation;

  constructor(
    private ngZone: NgZone,
    private location: Location,
    private toastService: ToastService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.currentUserTracker = Tracker.autorun(() => {
      const user = Users.findOne({
        _id: Meteor.userId(),
        role: RoleEnum.DISPO,
      }) as DispoUser;

      let customer = Customers.findOne({ _id: user?.customerId });
      this.ngZone.run(() => {
        if (
          user &&
          !user.dispoPermissions?.dispoAccess?.featurePermissions
            ?.isCustomerAdmin
        ) {
          this.historyBack();
        }
        this.customer$.next(customer);

        if (customer) {
          const sharedPoolCustomers = Customers.find({
            $or: [
              {
                'pool.jobsSharedWithCustomerIds': customer._id,
              },
              {
                'pool.employmentsSharedWithCustomerIds': customer._id,
              },
            ],
          }).fetch();
          this.sharedPoolCustomers$.next(sharedPoolCustomers);
        }

        this.user$.next(user);
        this.setAgencyPriorities();
      });
    });
  }
  ngOnDestroy() {
    this.currentUserTracker?.stop();
  }
  setAgencyPriorities(reset?: boolean) {
    if (reset) {
      this.activePriorities$.next([]);
    }

    const customer = this.customer$.value;
    const sharedPoolCustomers = this.sharedPoolCustomers$.value;

    if (this.jobTransferPriorities$.value.length === 0 || reset) {
      const jobTransferPriorities =
        customer?.customerTransfers?.transfers?.map((transfer) => ({
          customerName: transfer.targetCustomer.displayName,
          customerId: transfer.targetCustomer.id,
        })) || [];
      this.jobTransferPriorities$.next(jobTransferPriorities);
    }
    if (this.employmentTransferPriorities$.value.length === 0 || reset) {
      const employmentTransferPriorities =
        customer?.customerTransfers?.employmentTransfers?.map((transfer) => ({
          customerName: transfer.targetCustomer.displayName,
          customerId: transfer.targetCustomer.id,
        })) || [];
      this.employmentTransferPriorities$.next(employmentTransferPriorities);
    }
    if (this.sharedPoolPriorities$.value.length === 0 || reset) {
      let sharedPoolPriorities: { customerName: string; customerId: string }[];
      if (customer?.sharedPoolPriorities) {
        sharedPoolPriorities = sharedPoolCustomers
          .map((customer) => ({
            customerName: customer.name,
            customerId: customer._id,
          }))
          .sort((a, b) => {
            return (
              customer?.sharedPoolPriorities?.findIndex(
                (priority) => priority.customerId === a.customerId,
              ) ||
              0 -
                (customer?.sharedPoolPriorities?.findIndex(
                  (priority) => priority.customerId === b.customerId,
                ) || 0)
            );
          });
      } else {
        sharedPoolPriorities = sharedPoolCustomers.map((customer) => ({
          customerName: customer.name,
          customerId: customer._id,
        }));
      }
      this.sharedPoolPriorities$.next(sharedPoolPriorities);
    }

    switch (this._tab) {
      case 'TRANSFERS_JOBS':
        this.activePriorities$.next(this.jobTransferPriorities$.value);
        break;
      case 'TRANSFERS_EMPLOYMENTS':
        this.activePriorities$.next(this.employmentTransferPriorities$.value);
        break;
      case 'SHARED_POOL':
        this.activePriorities$.next(this.sharedPoolPriorities$.value);
        break;
    }
  }

  switchTab(tab: string): void {
    this._tab = tab;

    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: { tab: tab },
        replaceUrl: true,
        queryParamsHandling: 'merge',
      })
      .then();
  }

  cancelChanges() {
    this.setAgencyPriorities(true);
  }

  saveAgencyPriorities(): void {
    const customer = this.customer$.value;
    if (!customer) return;

    Meteor.call(
      'updateAgencyPriorities',
      customer._id,
      this.jobTransferPriorities$.value,
      this.employmentTransferPriorities$.value,
      this.sharedPoolPriorities$.value,
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
        } else {
          this.toastService.add({ type: 'SUCCESS', message: 'changesSaved' });
        }
      },
    );
  }

  historyBack() {
    this.location.back();
  }

  increasePriority(index: number) {
    const activePriorities = [...this.activePriorities$.value];
    let temp = activePriorities[index];
    activePriorities.splice(index, 1);
    activePriorities.splice(index - 1, 0, temp);
    this.activePriorities$.next(activePriorities);

    switch (this._tab) {
      case 'TRANSFERS_JOBS':
        this.jobTransferPriorities$.next(activePriorities);
        break;
      case 'TRANSFERS_EMPLOYMENTS':
        this.employmentTransferPriorities$.next(activePriorities);
        break;
      case 'SHARED_POOL':
        this.sharedPoolPriorities$.next(activePriorities);
        break;
    }
  }

  decreasePriority(index: number) {
    const activePriorities = [...this.activePriorities$.value];
    let temp = activePriorities[index];
    activePriorities.splice(index, 1);
    activePriorities.splice(index + 1, 0, temp);
    this.activePriorities$.next(activePriorities);

    switch (this._tab) {
      case 'TRANSFERS_JOBS':
        this.jobTransferPriorities$.next(activePriorities);
        break;
      case 'TRANSFERS_EMPLOYMENTS':
        this.employmentTransferPriorities$.next(activePriorities);
        break;
      case 'SHARED_POOL':
        this.sharedPoolPriorities$.next(activePriorities);
        break;
    }
  }
}
